import type { GameRenderContext, GameRenderer } from "../game";
import {
  CONNECT_FOUR_COLUMNS,
  CONNECT_FOUR_ROWS,
} from "./environment";
import type { ConnectFourState } from "./types";

export class ConnectFourRenderer implements GameRenderer<ConnectFourState> {
  #container: HTMLElement | null = null;
  #context: GameRenderContext<ConnectFourState> | null = null;

  mount(container: HTMLElement): void {
    if (this.#container) this.dispose();
    this.#container = container;
    this.#container.classList.add("connect-four-board");
    this.#container.setAttribute("aria-label", "Connect Four board");
    this.#container.addEventListener("click", this.#handleClick);
  }

  render(context: GameRenderContext<ConnectFourState>): void {
    if (!this.#container) throw new Error("Connect Four renderer is not mounted");
    this.#context = context;
    const columns: string[] = [];
    for (let column = 0; column < CONNECT_FOUR_COLUMNS; column += 1) {
      const cells: string[] = [];
      let lastRow = -1;
      if (context.lastModelAction?.action === column) {
        for (let row = 0; row < CONNECT_FOUR_ROWS; row += 1) {
          if (
            context.state.board[row * CONNECT_FOUR_COLUMNS + column] ===
              context.lastModelAction.player
          ) {
            lastRow = row;
            break;
          }
        }
      }
      for (let row = 0; row < CONNECT_FOUR_ROWS; row += 1) {
        const stone = context.state.board[row * CONNECT_FOUR_COLUMNS + column];
        const classes = [
          "connect-four-cell",
          stone === 0 ? "player-one" : "",
          stone === 1 ? "player-two" : "",
          row === lastRow ? "ai-move" : "",
        ].filter(Boolean).join(" ");
        cells.push(`<span class="${classes}"></span>`);
      }
      columns.push(`
        <button class="connect-four-column" data-action="${column}"
          role="gridcell" aria-label="Column ${column + 1}"
          ${context.interactive ? "" : "disabled"}>
          ${cells.join("")}
        </button>`);
    }
    this.#container.innerHTML = columns.join("");
  }

  dispose(): void {
    this.#container?.removeEventListener("click", this.#handleClick);
    this.#container?.classList.remove("connect-four-board");
    this.#container?.removeAttribute("aria-label");
    if (this.#container) this.#container.innerHTML = "";
    this.#container = null;
    this.#context = null;
  }

  #handleClick = (event: Event): void => {
    if (!this.#context?.interactive) return;
    const button = (event.target as HTMLElement)
      .closest<HTMLButtonElement>("[data-action]");
    if (!button) return;
    this.#context.onAction(Number(button.dataset.action));
  };
}
